import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../../../utils/api";
import { fetchGeneratedChallans, verifyPaidChallan } from "./challanApi";
import { updateFeeStatus } from "./feeApi";
import { C, fmt, Icon, Btn, Badge, Card, CardHeader, TH, TD } from "./feeUI";
import { alertDialog, confirmDialog } from "../../../utils/dialogs";

const fmtPKR = (n) => `PKR ${fmt(n)}`;

const statusBadge = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "paid") return <Badge color="green">Paid</Badge>;
  if (s.includes("pending")) return <Badge color="blue">Pending Verification</Badge>;
  return <Badge color="amber">{status || "Unpaid"}</Badge>;
};

/**
 * Accountant / Admin / Superadmin: review parent-uploaded paid challan proofs
 * and mark them as Paid after verification.
 */
export default function ChallanVerificationView({ campusId, onFeesChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | all | paid
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let list = await fetchGeneratedChallans();
      if (campusId != null && campusId !== "" && campusId !== "all") {
        list = list.filter((c) => String(c.campusId) === String(campusId));
      }
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      setRows([]);
      await alertDialog(err.message || "Could not load challans", { title: "Error" });
    } finally {
      setLoading(false);
    }
  }, [campusId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((c) => {
      const hasProof = !!c.paidProofPath;
      if (!hasProof) return false;
      const status = String(c.status || "").toLowerCase();
      if (filter === "pending" && !status.includes("pending")) return false;
      if (filter === "paid" && status !== "paid") return false;
      if (!q) return true;
      const hay = [
        c.challanNumber,
        c.studentName,
        c.studentRollNo,
        c.categoryLabel,
        c.month,
        c.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filter, search]);

  const pendingCount = useMemo(
    () =>
      rows.filter(
        (c) =>
          c.paidProofPath &&
          String(c.status || "")
            .toLowerCase()
            .includes("pending"),
      ).length,
    [rows],
  );

  const proofUrl = (path) => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const handleVerify = async (challan) => {
    const ok = await confirmDialog(
      `Mark challan ${challan.challanNumber} as Paid after verifying the uploaded proof?`,
      { title: "Verify & Mark Paid", confirmLabel: "Mark Paid" },
    );
    if (!ok) return;

    setBusyId(challan.id);
    try {
      const updated = await verifyPaidChallan(challan.id);
      // Keep fee-assignment store in sync so management/reports show Paid
      if (challan.studentId && challan.feeAssignmentKey) {
        const key = challan.feeAssignmentKey;
        const month = challan.month;
        const categoryId = challan.categoryId || (key.includes("_") ? key.split("_").slice(0, -1).join("_") : key);
        try {
          await updateFeeStatus(challan.studentId, categoryId, month, "Paid", {
            feeId: key,
            paymentMethod: "challan-proof",
            remarks: "Verified from parent-uploaded paid challan",
          });
        } catch {
          /* fee store may not have this assignment locally */
        }
      }
      setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
      if (typeof onFeesChanged === "function") await onFeesChanged();
      await alertDialog(`Challan ${updated.challanNumber} marked as Paid.`, {
        title: "Verified",
        variant: "success",
      });
    } catch (err) {
      await alertDialog(err.message || "Verification failed", { title: "Error" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Challan Verification"
        right={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {pendingCount > 0 && <Badge color="blue">{pendingCount} pending</Badge>}
            <Btn size="sm" variant="ghost" onClick={load}>
              Refresh
            </Btn>
          </div>
        }
      />

      <div style={{ padding: "16px 20px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { id: "pending", label: "Pending" },
            { id: "paid", label: "Verified Paid" },
            { id: "all", label: "All with proof" },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              style={{
                border: `1px solid ${filter === f.id ? C.navy : C.border}`,
                background: filter === f.id ? C.navy : C.white,
                color: filter === f.id ? "#fff" : C.textMid,
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search student / challan…"
          style={{
            flex: "1 1 220px",
            minWidth: 180,
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            fontSize: 13,
          }}
        />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: C.slate }}>Loading uploaded challans…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: C.slate }}>
          {filter === "pending"
            ? "No parent-uploaded challans awaiting verification."
            : "No uploaded challan proofs match this filter."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.bg }}>
                <TH>Student</TH>
                <TH>Challan</TH>
                <TH>Category / Month</TH>
                <TH>Amount</TH>
                <TH>Status</TH>
                <TH>Uploaded</TH>
                <TH>Action</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const isPaid = String(c.status || "").toLowerCase() === "paid";
                const url = proofUrl(c.paidProofPath);
                return (
                  <tr key={c.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <TD>
                      <div style={{ fontWeight: 700 }}>{c.studentName || "—"}</div>
                      <div style={{ fontSize: 11, color: C.slate }}>
                        {c.studentRollNo}
                        {c.studentClass ? ` · ${c.studentClass}` : ""}
                        {c.studentSection ? `-${c.studentSection}` : ""}
                      </div>
                    </TD>
                    <TD>
                      <span style={{ fontFamily: "monospace", fontSize: 12 }}>{c.challanNumber}</span>
                    </TD>
                    <TD>
                      <div>{c.categoryLabel || "Fee"}</div>
                      <div style={{ fontSize: 11, color: C.slate }}>{c.month || "—"}</div>
                    </TD>
                    <TD>
                      <span style={{ fontWeight: 700 }}>{fmtPKR(c.netPayable)}</span>
                    </TD>
                    <TD>{statusBadge(c.status)}</TD>
                    <TD>
                      <div style={{ fontSize: 12 }}>{c.paidProofName || "Proof"}</div>
                      <div style={{ fontSize: 11, color: C.slate }}>
                        {c.paidProofUploadedAt
                          ? new Date(c.paidProofUploadedAt).toLocaleString()
                          : "—"}
                      </div>
                      {isPaid && c.verifiedByName && (
                        <div style={{ fontSize: 11, color: C.green, marginTop: 2 }}>
                          by {c.verifiedByName}
                        </div>
                      )}
                    </TD>
                    <TD>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Btn
                          size="sm"
                          variant="ghost"
                          onClick={() => setPreview({ url, name: c.paidProofName, mime: c.paidProofMime })}
                          disabled={!url}
                        >
                          <Icon.Eye /> View
                        </Btn>
                        {!isPaid && (
                          <Btn
                            size="sm"
                            variant="success"
                            disabled={busyId === c.id}
                            onClick={() => handleVerify(c)}
                          >
                            {busyId === c.id ? "Saving…" : "Verify & Mark Paid"}
                          </Btn>
                        )}
                      </div>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,29,54,0.55)",
            zIndex: 2000,
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              width: "min(920px, 96vw)",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
            }}
          >
            <div
              style={{
                padding: "14px 18px",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: C.navy,
                color: "#fff",
              }}
            >
              <strong>{preview.name || "Paid challan proof"}</strong>
              <div style={{ display: "flex", gap: 8 }}>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}
                >
                  Open
                </a>
                <Btn size="sm" variant="ghost" onClick={() => setPreview(null)} style={{ color: C.navy }}>
                  Close
                </Btn>
              </div>
            </div>
            <div style={{ padding: 12, overflow: "auto", background: C.bg, flex: 1 }}>
              {String(preview.mime || preview.url || "").toLowerCase().includes("pdf") ? (
                <iframe title="Paid proof" src={preview.url} style={{ width: "100%", height: "70vh", border: "none", borderRadius: 8, background: "#fff" }} />
              ) : (
                <img
                  src={preview.url}
                  alt={preview.name || "Paid challan"}
                  style={{ maxWidth: "100%", display: "block", margin: "0 auto", borderRadius: 8 }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
